import React from 'react';
import useENSResolver from '../hooks/useENSResolver';

// Consistent color generation (same as Zhat)
const getAvatarColor = (seed) => {
  if (!seed) return '#800000'; // Default dark-red
  
  const colors = [
    '#800000', // dark red (primary)
    '#FFD700', // gold (accent)
    '#006400', // dark green
    '#00008B', // dark blue
    '#8B008B', // dark magenta
    '#556B2F', // dark olive
    '#483D8B', // dark slate blue
    '#2F4F4F', // dark slate gray
  ];
  
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

// Generate initials for fallback
const getFallbackInitials = (ensName, address) => {
  if (ensName) {
    const namePart = ensName.split('.')[0];
    return namePart.charAt(0).toUpperCase();
  } else if (address) {
    return address.charAt(2).toUpperCase();
  }
  return '?';
};

const AvatarDisplay = ({ 
  ensName, 
  address, 
  avatarUrl: propAvatarUrl = null, // NEW: Accept pre-fetched avatar URL
  size = 32, 
  showStatus = false,
  isOnline = false,
  onClick = null 
}) => {
  // Use our optimized hook - but prioritize propAvatarUrl if provided
  const { ensName: resolvedENS, avatarUrl: hookAvatarUrl, isLoading, error } = 
    useENSResolver(address, ensName);
  
  const displayENS = ensName || resolvedENS;
  
  // PRIORITY: Use propAvatarUrl if provided (cached from server), otherwise use hook result
  const avatarUrl = propAvatarUrl || hookAvatarUrl;
  
  const color = getAvatarColor(displayENS || address || '');
  const initials = getFallbackInitials(displayENS, address);
  const showError = error && !avatarUrl;

  // Loading state - clean spinner
  if (isLoading && !propAvatarUrl) { // Only show loading if we don't have cached avatar
    return (
      <div 
        onClick={onClick}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: 'var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-secondary)',
          fontSize: size * 0.4,
          border: `1px solid var(--border)`,
          position: 'relative',
          overflow: 'hidden',
          cursor: onClick ? 'pointer' : 'default'
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(90deg, 
            transparent 0%, 
            rgba(255, 215, 0, 0.1) 50%, 
            transparent 100%)`,
          animation: 'shimmer 1.5s infinite'
        }}></div>
        <span style={{ position: 'relative', zIndex: 1 }}>⋯</span>
      </div>
    );
  }

  // Avatar image from cache or ENS
  if (avatarUrl) {
    return (
      <div 
        onClick={onClick}
        style={{ 
          position: 'relative',
          cursor: onClick ? 'pointer' : 'default'
        }}
      >
        <img
          src={avatarUrl}
          alt={`${displayENS || address || 'User'} avatar`}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            border: `1px solid ${showError ? '#ff4444' : 'var(--border)'}`,
            backgroundColor: color,
            display: 'block'
          }}
          onError={(e) => {
            // If image fails, hide it and let fallback show
            e.target.style.display = 'none';
            console.warn(`Avatar failed to load: ${avatarUrl}`);
          }}
        />
        
        {/* Online status indicator */}
        {showStatus && (
          <div style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-2px',
            width: size * 0.2,
            height: size * 0.2,
            borderRadius: '50%',
            background: isOnline ? '#00ff88' : '#ff4444',
            border: '2px solid var(--black)'
          }}></div>
        )}
        
        {/* Error indicator */}
        {showError && (
          <div style={{
            position: 'absolute',
            top: '-2px',
            right: '-2px',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#ff4444',
            border: '1px solid var(--black)'
          }}></div>
        )}
      </div>
    );
  }

  // Fallback avatar with initials (clean, no emojis)
  return (
    <div 
      onClick={onClick}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-light)',
        fontSize: size * 0.4,
        fontWeight: 'bold',
        border: `1px solid ${showError ? '#ff4444' : 'var(--border)'}`,
        textTransform: 'uppercase',
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default'
      }}
    >
      {initials}
      
      {/* Online status indicator */}
      {showStatus && (
        <div style={{
          position: 'absolute',
          bottom: '-2px',
          right: '-2px',
          width: size * 0.2,
          height: size * 0.2,
          borderRadius: '50%',
          background: isOnline ? '#00ff88' : '#ff4444',
          border: '2px solid var(--black)'
        }}></div>
      )}
      
      {/* Error indicator */}
      {showError && (
        <div style={{
          position: 'absolute',
          top: '-2px',
          right: '-2px',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#ff4444',
          border: '1px solid var(--black)'
        }}></div>
      )}
    </div>
  );
};

// Add CSS animation for shimmer effect
const AvatarStyles = () => (
  <style>
    {`
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `}
  </style>
);

AvatarDisplay.Styles = AvatarStyles;

export default AvatarDisplay;
